import {IWindowService} from './window.service.interface';
import {isAbsolutUrl} from '../../../utils/url-utils';
import {WindowEventListenerRegistration} from './window-event-listener-registration';
import {action, computed, observable} from 'mobx';

export class WindowService implements IWindowService {

  constructor() {
    this._eventListenerRegistration = new WindowEventListenerRegistration(window);
  }
  getGlobalProperty(name: string): any {
    return window[name];
  }

  setGlobalProperty(name: string, value: any): void {
    window[name] = value;
  }

  getElementWidth(element: Element): number {
    return parseInt(window.getComputedStyle(element).width.replace('px', ''));
  }

  getElementHorizontalTranslationAmount(element: Element): number {
    let elementTransform = window.getComputedStyle(element).getPropertyValue("-webkit-transform") || window.getComputedStyle(element).getPropertyValue("transform");
    // we need split(",")[12] and [4] to support ie
    return parseInt(elementTransform.split(",")[12] || elementTransform.split(",")[4]) || 0;
  }

  get location(): Location {
    return window.location;
  }

  private readonly _eventListenerRegistration: WindowEventListenerRegistration;
  get subscribeTo(): WindowEventListenerRegistration {
    return this._eventListenerRegistration;
  }

  get innerWidth(): number {
    return window.innerWidth;
  }

  get innerHeight(): number {
    return window.innerHeight;
  }

  open(url?: string, target?: string, features?: string): void {
    window.open(url, target, features);
  }

  openSelf(url?: string): void {
    this.open(url, '_self')
  }

  get origin(): string {
    return window.location.origin;
  }

  get pathname(): string {
    return window.location.pathname;
  }

  get history(): History {
    return window.history;
  }

  scrollTo(x: number, y: number): void {
    this.scrollContainer.scrollTo(x, y);
  }

  get scrollY(): number {
    return window.scrollY;
  }

  scrollBy(x: number, y: number): void {
    this.scrollContainer.scrollBy(x, y)
  }

  isAbsolutUrl(url: string): boolean {
    return isAbsolutUrl(url);
  }

  @observable.ref _scrollContainer: HTMLElement = undefined;

  @computed get scrollContainer(): any {
    if(this._scrollContainer) {
      return this._scrollContainer;
    }

    return window;
  }

  @action.bound
  setScrollContainer(element: HTMLElement): void {
    this._scrollContainer = element;

    this._eventListenerRegistration.setScrollContainer(element);

    Object.defineProperty(window, 'scrollY', {
        get: () => this._scrollContainer ? this._scrollContainer.scrollTop : window.scrollY,
        configurable: true
    });

    Object.defineProperty(window, 'pageYOffset', {
        get: () => this._scrollContainer ? this._scrollContainer.scrollTop : window.pageYOffset,
        configurable: true
    });

    Object.defineProperty(document.body, 'scrollTop', {
        get: () => this._scrollContainer ? this._scrollContainer.scrollTop : document.body.scrollTop,
        set: (value) => {
          if(this._scrollContainer) {
            this._scrollContainer.scrollTop = value;
          } else {
            document.body.scrollTop = value;
          }
        },
        configurable: true
    });

    if(document.documentElement) {
        Object.defineProperty(document.documentElement, 'scrollTop', {
            get: () => this._scrollContainer ? this._scrollContainer.scrollTop : document.documentElement.scrollTop,
            set: (value) => {
              if(this._scrollContainer) {
                this._scrollContainer.scrollTop = value;
              } else {
                document.documentElement.scrollTop = value;
              }
            },
            configurable: true
        });
    }

  }

  get scrollTop(): number {
      if(this._scrollContainer) {
          return this._scrollContainer.scrollTop;
      }
      return (document.documentElement && document.documentElement.scrollTop) || document.body.scrollTop;
  }

}
